import { Link, Radio, RadioGroup, Text, Title3, makeStyles } from '@fluentui/react-components';
import { getStepType, healthChoiceSubmissionSchema, type ConfigOf, type SubmissionOf } from '@vigeo/shared';
import { tokens } from '@vigeo/ui';
import type { StepPortalProps, StepReviewProps, StepUiModule } from '../contract.js';

const useStyles = makeStyles({
  block: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  intro: { whiteSpace: 'pre-wrap' },
  group: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  note: { color: tokens.colorNeutralForeground3 },
});

const kernel = getStepType('health-choice');
type Config = ConfigOf<'health-choice'>;
type Submission = SubmissionOf<'health-choice'>;

/**
 * The candidate's half of a health choice (06b form 6).
 *
 * THE ONE THING THIS COMPONENT MUST NOT GET WRONG. Changing the answer has to clear a reason
 * that no longer applies. The server refuses a reason attached to a choice that takes none —
 * deliberately, so a record never holds something nobody asked for — so leaving a stale reason
 * in the draft would leave the person pressing Send against a refusal they cannot see the cause
 * of, having answered the visible questions correctly.
 */
function HealthChoicePortal({ config, draft, onDraftChange, disabled }: StepPortalProps) {
  const styles = useStyles();
  const parsed = kernel.configSchema.safeParse(config);
  if (!parsed.success) return <Text>This step is misconfigured. HR has been notified.</Text>;

  const settings: Config = parsed.data;
  // The concrete schema, not `kernel.submissionSchema`: the erased registry type has no `.partial()`,
  // and a draft is half-typed by design (ADR-013 §3).
  const current = healthChoiceSubmissionSchema.partial().safeParse(draft);
  const choice = current.success ? (current.data.choice ?? null) : null;
  const reason = current.success ? (current.data.reason ?? null) : null;
  const chosen = settings.choices.find((option) => option.key === choice);

  const write = (next: { choice: string | null; reason: string | null }) =>
    onDraftChange(next.choice === null ? null : ({ choice: next.choice, reason: next.reason } satisfies Submission));

  return (
    <div className={styles.block}>
      <Title3>{settings.heading}</Title3>
      <Text className={styles.intro}>{settings.intro}</Text>
      {settings.policyUrl !== null && settings.policyLabel !== null ? (
        <Link href={settings.policyUrl} target="_blank" rel="noreferrer">
          {settings.policyLabel}
        </Link>
      ) : null}

      <div className={styles.group}>
        <Text weight="semibold" id="health-choice-status">
          {settings.statusPrompt}
        </Text>
        <RadioGroup
          aria-labelledby="health-choice-status"
          value={choice ?? ''}
          disabled={disabled}
          onChange={(_, d) => {
            const next = settings.choices.find((option) => option.key === d.value);
            // Carry the reason over only when the new choice still asks for one.
            write({ choice: d.value, reason: next?.requiresReason === true ? reason : null });
          }}
        >
          {settings.choices.map((option) => (
            <Radio key={option.key} value={option.key} label={option.label} />
          ))}
        </RadioGroup>
      </div>

      {chosen?.requiresReason === true ? (
        <div className={styles.group}>
          <Text weight="semibold" id="health-choice-reason">
            {settings.reasonPrompt}
          </Text>
          <RadioGroup
            aria-labelledby="health-choice-reason"
            value={reason ?? ''}
            disabled={disabled}
            onChange={(_, d) => write({ choice, reason: d.value })}
          >
            {settings.reasons.map((option) => (
              <Radio key={option.key} value={option.key} label={option.label} />
            ))}
          </RadioGroup>
          {/*
            The reasons are a closed list for this reason, and saying so is part of the design:
            there is nowhere to type a medical detail, and nobody should try.
          */}
          <Text size={200} className={styles.note}>
            Please do not include medical details. Choose the closest reason.
          </Text>
        </div>
      ) : null}
    </div>
  );
}

function HealthChoiceReview({ config, submission }: StepReviewProps) {
  const styles = useStyles();
  const settings = kernel.configSchema.safeParse(config);
  const answer = kernel.submissionSchema.safeParse(submission);

  if (!settings.success || !answer.success) {
    return (
      <div className={styles.block}>
        <Text weight="semibold">Health choice</Text>
        <Text>No answer recorded.</Text>
      </div>
    );
  }

  const chosen = settings.data.choices.find((option) => option.key === answer.data.choice);
  const reason = settings.data.reasons.find((option) => option.key === answer.data.reason);

  return (
    <div className={styles.block}>
      <Text weight="semibold">{settings.data.heading}</Text>
      {/* The stored key is shown when a label has since been removed, rather than nothing at all. */}
      <Text>{chosen?.label ?? answer.data.choice}</Text>
      {answer.data.reason !== null ? <Text>{reason?.label ?? answer.data.reason}</Text> : null}
    </div>
  );
}

export const healthChoiceUi: StepUiModule = { portal: HealthChoicePortal, review: HealthChoiceReview };
